import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import {
	Container,
	Button,
	Paper,
	Typography,
	IconButton,
	TextField,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Box,
	Divider
} from '@mui/material';
import { Stack } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleLeft } from '@fortawesome/free-solid-svg-icons';
import { Save } from '@mui/icons-material';
import { DataContext } from '../../../../context/DataContext';
import { getRuta, simpleMessage } from '../../../../helpers/Helpers';
import { getToken } from '../../../../api/Account';
import { useNavigate, useParams } from 'react-router-dom';
import {
	addAsignacionesAsync,
	addDeduccionesAsync,
	getNominaDetailAsync
} from '../../../../api/Employees';
import moment from 'moment/moment';
import { isEmpty } from 'lodash';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { PrintReport } from '../../../../components/PrintReport';

// Constantes fuera del componente
const NUMBER_REGEX = /^\d*\.?\d*$/;
const CURRENCY_FORMATTER = new Intl.NumberFormat('es-NI', {
	style: 'currency',
	currency: 'NIO'
});

// Componente de input reutilizable
const NumberInput = ({ value, onChange, label, disabled, ...props }) => {
	const handleChange = useCallback(
		(e) => {
			const newValue = e.target.value;
			if (NUMBER_REGEX.test(newValue.toString()) || newValue === '') {
				onChange(newValue);
			}
		},
		[onChange]
	);

	return (
		<TextField
			fullWidth
			size="small"
			disabled={disabled}
			onChange={handleChange}
			label={label}
			value={value}
			{...props}
		/>
	);
};

// Componente de campo con botón de guardar
const SaveableField = ({ value, onChange, label, disabled, onSave, ...props }) => (
	<NumberInput
		value={value}
		onChange={onChange}
		label={label}
		disabled={disabled}
		InputProps={{
			startAdornment: <InputAdornment position="start">C$</InputAdornment>,
			endAdornment: (
				<InputAdornment position="end">
					<IconButton disabled={disabled} onClick={onSave}>
						<Save style={{ color: '#2979ff' }} />
					</IconButton>
				</InputAdornment>
			)
		}}
		{...props}
	/>
);

// Componente de campo solo lectura
const DisplayField = ({ label, value }) => (
	<TextField
		fullWidth
		disabled
		size="small"
		label={label}
		value={value}
		InputProps={{
			startAdornment: <InputAdornment position="start">C$</InputAdornment>
		}}
	/>
);

const NominaEmployee = () => {
	const [detalleNomina, setDetalleNomina] = useState(null);
	const [formValues, setFormValues] = useState({
		montoBono: 0,
		montoComision: 0,
		montoViaticoAlimentacion: 0,
		montoViaticoTransporte: 0,
		hExtra: 0,
		diaFeriado: 0,
		montoOtrasDeducciones: 0
	});
	const [isCerrado, setIsCerrado] = useState(false);
	const [localReload, setLocalReload] = useState(false);

	const compRef = useRef();
	const navigate = useNavigate();
	const { setIsLoading, setIsUnautorized } = useContext(DataContext);
	const { id, ideployee } = useParams();

	const token = useMemo(() => getToken(), []);
	const ruta = useMemo(() => getRuta(), []);

	// Manejadores de cambio unificados
	const handleFieldChange = useCallback(
		(field) => (value) => {
			setFormValues((prev) => ({ ...prev, [field]: value }));
		},
		[]
	);

	// Función para obtener los detalles de la nómina
	const getNomina = useCallback(async () => {
		if (!ideployee) return;

		setIsLoading(true);
		try {
			const result = await getNominaDetailAsync(token, ideployee);
			if (!result.statusResponse) {
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}

			const { data } = result;
			setIsCerrado(data.result.nomina.estado.id === 2);
			setDetalleNomina(data.result);

			// Actualizar valores del formulario
			setFormValues({
				montoBono: data.result.bono || 0,
				montoComision: data.result.comision || 0,
				montoViaticoAlimentacion: data.result.montoViaticoAlimentacion || 0,
				montoViaticoTransporte: data.result.montoViaticoTransporte || 0,
				hExtra: data.result.horasExtra || 0,
				diaFeriado: data.result.diasFeriado || 0,
				montoOtrasDeducciones: data.result.otrasDeducciones || 0
			});
		} catch (error) {
			simpleMessage('Error al cargar los datos', 'error');
		} finally {
			setIsLoading(false);
		}
	}, [ideployee, token, setIsLoading, setIsUnautorized]);

	useEffect(() => {
		getNomina();
	}, [getNomina, localReload]);

	// Funciones de guardado optimizadas
	const saveAsignacion = useCallback(async () => {
		if (formValues.montoBono.length === 0) {
			simpleMessage('Ingrese un valor de bono...', 'error');
			return;
		}

		setIsLoading(true);
		try {
			const data = {
				id: ideployee,
				...formValues
			};
			const result = await addAsignacionesAsync(token, data);

			if (!result.statusResponse) {
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}

			simpleMessage('Asignación Agregada...!', 'success');
			setLocalReload((prev) => !prev);
		} catch (error) {
			simpleMessage('Error al guardar asignación', 'error');
		} finally {
			setIsLoading(false);
		}
	}, [formValues, ideployee, token, setIsLoading, setIsUnautorized]);

	const saveDeducciones = useCallback(async () => {
		if (formValues.montoBono.length === 0) {
			simpleMessage('Ingrese un valor de bono...', 'error');
			return;
		}

		setIsLoading(true);
		try {
			const data = {
				id: ideployee,
				montoOtrasDeducciones: formValues.montoOtrasDeducciones
			};
			const result = await addDeduccionesAsync(token, data);

			if (!result.statusResponse) {
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}

			simpleMessage('Deducción Agregada...!', 'success');
			setLocalReload((prev) => !prev);
		} catch (error) {
			simpleMessage('Error al guardar deducción', 'error');
		} finally {
			setIsLoading(false);
		}
	}, [
		formValues.montoOtrasDeducciones,
		formValues.montoBono,
		ideployee,
		token,
		setIsLoading,
		setIsUnautorized
	]);

	// Cálculos memorizados
	const totalAsignaciones = useMemo(() => {
		if (!detalleNomina) return 0;
		return (
			detalleNomina.salario +
			Number(formValues.montoBono || 0) +
			Number(formValues.montoComision || 0) +
			detalleNomina.montoHorasExtra +
			detalleNomina.montoDiasFeriado +
			detalleNomina.montoViaticoAlimentacion +
			detalleNomina.montoViaticoTransporte
		);
	}, [detalleNomina, formValues]);

	const totalDeducciones = useMemo(() => {
		if (!detalleNomina) return 0;
		return (
			detalleNomina.aporteINSS +
			detalleNomina.aporteIR +
			detalleNomina.adelanto +
			detalleNomina.prestamo +
			detalleNomina.otrasDeducciones
		);
	}, [detalleNomina]);

	const totalAcumulado = useMemo(() => {
		if (!detalleNomina) return 0;
		return detalleNomina.indemnizacion + detalleNomina.vacaciones + detalleNomina.aguinado;
	}, [detalleNomina]);

	const pageStyle = `
		@media print {
			html, body {
				height: initial !important;
				overflow: initial !important;
				-webkit-print-color-adjust: exact;
			}
			.pagebreak {
				page-break-before: always;
			}
		}
		@media all {
			.pagebreak {
				display: none;
			}
		}
		@page { margin: 5mm; }
	`;

	if (isEmpty(detalleNomina)) return null;

	const {
		montoBono,
		montoComision,
		montoViaticoAlimentacion,
		montoViaticoTransporte,
		hExtra,
		diaFeriado,
		montoOtrasDeducciones
	} = formValues;

	return (
		<div>
			<Container maxWidth="xl">
				<Paper style={{ padding: 20, textAlign: 'center' }}>
					{/* Header */}
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						spacing={1}
					>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Button
								onClick={() => navigate(`${ruta}/nominadetails/${id}`)}
								style={{ color: '#2979ff', borderColor: '#2979ff' }}
								variant="outlined"
							>
								<FontAwesomeIcon
									style={{ marginRight: 10, fontSize: 20 }}
									icon={faChevronCircleLeft}
								/>
								Regresar
							</Button>
							<Typography color="#00a152" variant="h5" fontWeight="bold">
								DETALLE NOMINA EMPLEADO
							</Typography>
						</Stack>
						<ReactToPrint
							trigger={() => (
								<IconButton variant="outlined">
									<PrintRoundedIcon
										style={{ fontSize: 35, color: '#2979ff', width: 35 }}
									/>
								</IconButton>
							)}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>
					<Divider sx={{ my: 2 }} />

					{/* Employee Info */}
					<Paper style={{ padding: 20 }}>
						<Stack
							spacing={1}
							direction={{ xs: 'column', sm: 'column', md: 'row' }}
							justifyContent="space-between"
						>
							<Stack spacing={1}>
								<Stack spacing={1} direction="row">
									<Typography color="#ffc400">No EMPLEADO:</Typography>
									<Typography>{detalleNomina.empleado.id}</Typography>
								</Stack>
								<Stack spacing={1} direction="row">
									<Typography color="#ffc400">NOMBRE Y APELLIDO:</Typography>
									<Typography>{detalleNomina.empleado.fullName}</Typography>
								</Stack>
							</Stack>
							<Stack spacing={1}>
								<Stack spacing={1} direction="row">
									<Typography color="#ffc400">TASA ORDINARIA:</Typography>
									<Typography>
										{CURRENCY_FORMATTER.format(detalleNomina.empleado.salario)}
									</Typography>
								</Stack>
								<Stack spacing={1} direction="row">
									<Typography color="#ffc400">PERIODO:</Typography>
									<Typography>
										DESDE {moment(detalleNomina.desde).format('L')} HASTA{' '}
										{moment(detalleNomina.hasta).format('L')}
									</Typography>
								</Stack>
							</Stack>
							<Stack spacing={1}>
								<Stack spacing={1} direction="row">
									<Typography color="#ffc400">No. RECIBO:</Typography>
									<Typography>{detalleNomina.id}</Typography>
								</Stack>
								<Stack spacing={1} direction="row">
									<Typography color="#ffc400">FECHA PAGO:</Typography>
									<Typography>
										{moment(detalleNomina.hasta).format('L')}
									</Typography>
								</Stack>
							</Stack>
						</Stack>
					</Paper>

					{/* Main Content */}
					<Stack
						spacing={1}
						marginTop={2}
						direction={{ xs: 'column', sm: 'column', md: 'row' }}
						justifyContent="space-between"
					>
						{/* Asignaciones */}
						<Stack spacing={1} flex={1}>
							<Paper
								component={Stack}
								justifyContent="space-between"
								style={{ padding: 20, height: 450 }}
							>
								<Stack>
									<Typography color="#00a152" fontWeight="bold">
										ASIGNACIONES
									</Typography>
									<Divider sx={{ my: 1 }} />
									<Stack spacing={1}>
										<DisplayField
											label="Ordinario"
											value={detalleNomina.salario}
										/>

										<Stack
											direction={{ xs: 'column', sm: 'column', md: 'row' }}
											spacing={2}
										>
											<SaveableField
												value={diaFeriado}
												onChange={handleFieldChange('diaFeriado')}
												label="Dia Feriado"
												disabled={isCerrado}
												onSave={saveAsignacion}
											/>
											<DisplayField
												label="Monto Dia Feriado"
												value={detalleNomina.montoDiasFeriado}
											/>
										</Stack>

										<Stack
											direction={{ xs: 'column', sm: 'column', md: 'row' }}
											spacing={2}
										>
											<SaveableField
												value={hExtra}
												onChange={handleFieldChange('hExtra')}
												label="Horas Extra"
												disabled={isCerrado}
												onSave={saveAsignacion}
											/>
											<DisplayField
												label="Monto Horas Extra"
												value={detalleNomina.montoHorasExtra}
											/>
										</Stack>

										<SaveableField
											value={montoBono}
											onChange={handleFieldChange('montoBono')}
											label="Monto Bono"
											disabled={isCerrado}
											onSave={saveAsignacion}
										/>

										<SaveableField
											value={montoViaticoAlimentacion}
											onChange={handleFieldChange('montoViaticoAlimentacion')}
											label="Monto Viatico Alimentacion"
											disabled={isCerrado}
											onSave={saveAsignacion}
										/>

										<SaveableField
											value={montoViaticoTransporte}
											onChange={handleFieldChange('montoViaticoTransporte')}
											label="Monto Viatico Transporte"
											disabled={isCerrado}
											onSave={saveAsignacion}
										/>
									</Stack>
								</Stack>

								<Stack>
									<Divider sx={{ my: 1 }} />
									<Stack
										spacing={1}
										direction="row"
										justifyContent="space-between"
									>
										<Typography color="#00a152" fontWeight="bold">
											TOTAL
										</Typography>
										<Typography fontWeight="bold">
											{CURRENCY_FORMATTER.format(totalAsignaciones)}
										</Typography>
									</Stack>
								</Stack>
							</Paper>
						</Stack>

						{/* Deducciones */}
						<Stack spacing={1} flex={1}>
							<Paper
								component={Stack}
								justifyContent="space-between"
								style={{ padding: 20, height: 450 }}
							>
								<Stack>
									<Typography color="#00a152" fontWeight="bold">
										DEDUCCIONES
									</Typography>
									<Divider sx={{ my: 1 }} />
									<Stack spacing={1}>
										<DisplayField
											label="Aporte INSS"
											value={detalleNomina.aporteINSS}
										/>
										<DisplayField
											label="Aporte IR"
											value={detalleNomina.aporteIR}
										/>
										<DisplayField
											label="Adelanto de Salario"
											value={detalleNomina.adelanto}
										/>
										<DisplayField
											label="Prestamo Efectivo"
											value={detalleNomina.prestamo}
										/>
										<DisplayField
											label="Constancia"
											value={detalleNomina.montoPermisos}
										/>
										<SaveableField
											value={montoOtrasDeducciones}
											onChange={handleFieldChange('montoOtrasDeducciones')}
											label="Otras Deducciones"
											disabled={isCerrado}
											onSave={saveDeducciones}
										/>
									</Stack>
								</Stack>
								<Stack>
									<Divider sx={{ my: 1 }} />
									<Stack
										spacing={1}
										direction="row"
										justifyContent="space-between"
										alignItems="center"
									>
										<Typography color="#00a152" fontWeight="bold">
											TOTAL
										</Typography>
										<Typography fontWeight="bold">
											{CURRENCY_FORMATTER.format(totalDeducciones)}
										</Typography>
									</Stack>
								</Stack>
							</Paper>
						</Stack>

						{/* Acumulado */}
						<Stack spacing={1} flex={1}>
							<Paper
								component={Stack}
								justifyContent="space-between"
								style={{ padding: 20, height: 450 }}
							>
								<Stack>
									<Typography color="#00a152" fontWeight="bold">
										ACUMULADO
									</Typography>
									<Divider sx={{ my: 1 }} />
									<Stack spacing={1}>
										<DisplayField
											label="Vacaciones"
											value={detalleNomina.vacaciones}
										/>
										<DisplayField
											label="Aguinaldo"
											value={detalleNomina.aguinado}
										/>
										<DisplayField
											label="Indemnización"
											value={detalleNomina.indemnizacion}
										/>
									</Stack>
								</Stack>
								<Stack>
									<Divider sx={{ my: 1 }} />
									<Stack
										spacing={1}
										direction="row"
										justifyContent="space-between"
										alignItems="center"
									>
										<Typography color="#00a152" fontWeight="bold">
											TOTAL
										</Typography>
										<Typography fontWeight="bold">
											{CURRENCY_FORMATTER.format(totalAcumulado)}
										</Typography>
									</Stack>
								</Stack>
							</Paper>
						</Stack>
					</Stack>

					{/* Total a Recibir */}
					<Paper style={{ marginTop: 20, padding: 20, textAlign: 'center' }}>
						<Stack spacing={1} direction="row" justifyContent="center">
							<Typography color="#00a152" fontWeight="bold">
								TOTAL A RECIBIR
							</Typography>
							<Typography fontWeight="bold">
								{CURRENCY_FORMATTER.format(detalleNomina.totalPercibir)}
							</Typography>
						</Stack>
					</Paper>
				</Paper>
			</Container>

			{/* Print Component */}
			<div style={{ display: 'none' }}>
				<PrintReport ref={compRef} titulo="RECIBO DE PAGO">
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<Box sx={{ maxWidth: 800, mx: 'auto', bgcolor: 'white' }}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									flexDirection: 'column'
								}}
							>
								<Box
									sx={{
										display: 'flex',
										gap: 10,
										justifyContent: 'space-between'
									}}
								>
									<Typography>
										Fecha Desde: {moment(detalleNomina.desde).format('L')}
									</Typography>
									<Typography>
										Fecha Hasta: {moment(detalleNomina.hasta).format('L')}
									</Typography>
								</Box>
								<Box
									sx={{
										display: 'flex',
										gap: 2,
										justifyContent: 'space-between'
									}}
								>
									<Typography>
										Empleado: {detalleNomina.empleado.id} -{' '}
										{detalleNomina.empleado.fullName} -<strong> Cargo:</strong>{' '}
										{detalleNomina.empleado.puesto.description}
									</Typography>
									<Typography variant="subtitle1">
										No. Seguro Social {detalleNomina.empleado.inssNumber}
									</Typography>
								</Box>
							</Box>

							<TableContainer sx={{ mb: 3, bgcolor: 'white' }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell
												sx={{
													fontWeight: 'bold',
													textAlign: 'center',
													color: 'black'
												}}
											>
												INGRESOS
											</TableCell>
											<TableCell
												sx={{
													fontWeight: 'bold',
													textAlign: 'center',
													color: 'black'
												}}
											>
												DEDUCCIONES
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										<TableRow>
											<TableCell
												sx={{ alignContent: 'start', color: 'black' }}
											>
												{[
													{
														label: 'Salario Ordinario:',
														value: detalleNomina.salario
													},
													{
														label: 'Dia Feriado:',
														value: detalleNomina.montoDiasFeriado
													},
													{
														label: 'Horas Extra:',
														value: detalleNomina.montoHorasExtra
													},
													{ label: 'Bono:', value: montoBono },
													{
														label: 'Viatico Alimentacion:',
														value: detalleNomina.montoViaticoAlimentacion
													},
													{
														label: 'Viatico Transporte:',
														value: detalleNomina.montoViaticoTransporte
													}
												].map((item, index) => (
													<Stack
														key={index}
														direction="row"
														justifyContent="space-between"
													>
														<Typography>{item.label}</Typography>
														<Typography>
															{CURRENCY_FORMATTER.format(item.value)}
														</Typography>
													</Stack>
												))}
											</TableCell>
											<TableCell
												sx={{ alignContent: 'start', color: 'black' }}
											>
												{[
													{
														label: 'Aporte INSS:',
														value: detalleNomina.aporteINSS
													},
													{
														label: 'Aporte IR:',
														value: detalleNomina.aporteIR
													},
													{
														label: 'Adelanto de Salario:',
														value: detalleNomina.adelanto
													},
													{
														label: 'Préstamo en Efectivo:',
														value: detalleNomina.prestamo
													},
													{
														label: 'Constancia:',
														value: detalleNomina.montoPermisos
													},
													{
														label: 'Otras Deducciones:',
														value: montoOtrasDeducciones
													}
												].map((item, index) => (
													<Stack
														key={index}
														direction="row"
														justifyContent="space-between"
													>
														<Typography>{item.label}</Typography>
														<Typography>
															{CURRENCY_FORMATTER.format(item.value)}
														</Typography>
													</Stack>
												))}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell
												sx={{ alignContent: 'start', color: 'black' }}
											>
												<Stack
													direction="row"
													justifyContent="space-between"
												>
													<Typography>
														<strong>Total Ingresos</strong>
													</Typography>
													<Typography>
														<strong>
															{CURRENCY_FORMATTER.format(
																totalAsignaciones
															)}
														</strong>
													</Typography>
												</Stack>
											</TableCell>
											<TableCell
												sx={{ alignContent: 'start', color: 'black' }}
											>
												<Stack
													direction="row"
													justifyContent="space-between"
												>
													<Typography>
														<strong>Total Egresos</strong>
													</Typography>
													<Typography>
														<strong>
															{CURRENCY_FORMATTER.format(
																totalDeducciones
															)}
														</strong>
													</Typography>
												</Stack>
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>

							<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
								<Stack
									direction="row"
									justifyContent="space-between"
									display="flex"
									flex={1}
								>
									<Typography>
										<strong>NETO A PAGAR:</strong>
									</Typography>
									<Typography style={{ marginRight: 15 }}>
										<strong>
											{CURRENCY_FORMATTER.format(detalleNomina.totalPercibir)}
										</strong>
									</Typography>
								</Stack>
								<Typography style={{ flex: 1, textAlign: 'right' }}>
									<strong>{detalleNomina.id}</strong>
								</Typography>
							</Box>
						</Box>
					</Container>
				</PrintReport>
			</div>
		</div>
	);
};

export default NominaEmployee;
